import * as React from 'react';
import Highcharts from 'highcharts';

declare module 'highcharts' {
  interface Point {
    highlight?: (e: PointerEvent | MouseEvent | TouchEvent) => void;
  }
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Synchronized Module
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const SyncModule = (H: typeof Highcharts): void => {
  H.Pointer.prototype.reset = function () {
    return undefined;
  };

  H.Point.prototype.highlight = function (event) {
    const _event = this.series.chart.pointer.normalize(event);
    this.onMouseOver();
    this.series.chart.tooltip.refresh(this);
    this.series.chart.xAxis[0].drawCrosshair(_event, this);
  };
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Synchronized Hook
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const synchronizedEventTypes = [
  'mousemove',
  'touchmove',
  'touchstart',
] as const;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useSync = (
  containerElementRef: React.RefObject<HTMLDivElement>,
  instancesMapRef: React.MutableRefObject<Map<string, Highcharts.Chart>>
) => {
  const onExtremesSync = function (
    this: Highcharts.Axis,
    e: Highcharts.AxisSetExtremesEventObject
  ): void {
    if (e.trigger !== undefined && e.trigger !== 'sync') {
      instancesMapRef.current.forEach((instance) => {
        if (instance && instance !== this.chart) {
          instance.xAxis[0]?.setExtremes?.(e.min, e.max, true, false, {
            trigger: 'sync',
            source: this.chart,
          });
        }
      });
    }
  };

  React.useLayoutEffect(() => {
    const synchronizedEventsHandler = (
      event: PointerEvent | MouseEvent | TouchEvent
    ) => {
      instancesMapRef.current.forEach((instance) => {
        if (!!instance?.options && !!instance?.pointer) {
          const _event = instance.pointer.normalize(event);
          const point = instance.series[0]?.searchPoint?.(_event, true);

          point?.highlight?.(_event);
        }
      });
    };

    synchronizedEventTypes.forEach((eventType) => {
      containerElementRef.current?.addEventListener(
        eventType,
        synchronizedEventsHandler
      );
    });

    return () => {
      synchronizedEventTypes.forEach((eventType) => {
        containerElementRef.current?.removeEventListener(
          eventType,
          synchronizedEventsHandler
        );
      });
    };
  });

  return { onExtremesSync };
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Module Exports
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export { SyncModule, useSync };
